import { useEffect, useState } from 'react';
import Config from '../../../../axios/Config';
import { USER_FID_CARD } from '../../../../constants/routes';
import {
  confirmWarning,
  notifyInfo,
  notifyWarning,
  notifyError,
} from '../../../../utils/Notify';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import FidScanScreen from './FidScanScreen';
import SuccessScreen from './components/SuccessScreen';
import { addFidCard } from '../../../../actions/userFidelityActions';

const UserAddFidScanScreen = () => {
  const navigate = useNavigate();
  const axiosInstance = Config();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const { cartItems } = useSelector((state) => state.cart);
  const [isLoading, setIsLoading] = useState(false);
  const [fidelityCardId, setFidelityCardId] = useState(undefined);
  const { state } = useLocation();
  const [isSuccess, setIsSuccess] = useState(false);

  const validateFidCard = (fidCardNum) => {
    if (!fidCardNum || typeof fidCardNum !== 'string') {
      throw new Error('Numéro de carte invalide');
    }
    if (fidCardNum.trim().length === 0) {
      throw new Error('Le numéro de carte ne peut pas être vide');
    }
    if (!state?.fidCard) {
      throw new Error('Information de la carte de fidélité manquante');
    }
  };

  const checkExistingCard = (fidCardNum) => {
    if (!user?.fidCard) return { isCardWillBeReplaced: false, isCardAlreadyAdded: false };

    const isCardWillBeReplaced = user.fidCard.some(
      (card) => state?.fidCard?._id === card.fidelityCardBddId
    );
    const isCardAlreadyAdded = user.fidCard.some(
      (card) => fidCardNum === card.fidelityCardPhysicalId
    );

    return { isCardWillBeReplaced, isCardAlreadyAdded };
  };

  const onVerification = (fidCardNum) => {
    try {
      validateFidCard(fidCardNum);

      const { isCardWillBeReplaced, isCardAlreadyAdded } = checkExistingCard(fidCardNum);

      if (isCardWillBeReplaced && isCardAlreadyAdded) {
        notifyInfo('Tu as déjà ajouté cette carte à ton compte.');
        return;
      }

      if (isCardWillBeReplaced) {
        confirmWarning(
          'Tu as déjà une carte de fidelité',
          'Tu possèdes déjà une carte de fidélité avec cette enseigne. Souhaites-tu la remplacer ?',
          'Oui',
          () => addCard(fidCardNum)
        );
        return;
      }

      addCard(fidCardNum);
    } catch (error) {
      notifyError('Erreur', error.message);
    }
  };

  const onSuccess = () => {
    setIsSuccess(true);
  };

  const handleSuccessConfirm = () => {
    navigate(USER_FID_CARD);
  };

  const onIdentifySuccess = async (fidCardNum, data) => {
    try {
      if (data.userMaskedEmail) {
        notifyWarning('Cette carte est déjà associée à un compte');
        return;
      }

      if (!user) {
        throw new Error('Informations utilisateur manquantes');
      }
      if (!user._id) {
        throw new Error('Informations utilisateur manquantes');
      }
      if (!state) {
        throw new Error('Informations entreprise manquantes');
      }
      if (!state.fidCard) {
        throw new Error('Informations entreprise manquantes');
      }
      if (!state.fidCard.companyId) {
        throw new Error('Informations entreprise manquantes');
      }

      dispatch(
        addFidCard(
          axiosInstance,
          user._id,
          cartItems,
          fidCardNum,
          state?.companyId,
          store?.id,
          () => { },
          false,
          onSuccess,
          setIsLoading
        )
      );
    } catch (error) {
      console.error('Error in onIdentifySuccess:: ', error);
      notifyError('Erreur', "Une erreur est survenue lors de l'ajout de la carte");
      setIsLoading(false);
    }
  };

  const addCard = (fidCardNum) => {
    setIsLoading(true);
    onIdentifySuccess(fidCardNum, { userMaskedEmail: false });
  };

  const goToCardScreen = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (fidelityCardId) {
      onVerification(fidelityCardId);
      setFidelityCardId(undefined);
    }
  }, [fidelityCardId]);

  if (isSuccess) {
    return <SuccessScreen onConfirm={handleSuccessConfirm} />;
  }

  return (
    <FidScanScreen
      onScan={setFidelityCardId}
      fidScanLoading={isLoading}
      goToCardScreen={goToCardScreen}
      companyLogo={state?.fidCard?.companyImage}
      isSuccess={isSuccess}
      setIsSuccess={setIsSuccess}
    />
  );
};

export default UserAddFidScanScreen;
